import {Component, EventEmitter, Input, Output} from "@angular/core";
import {Device} from "../device";
import {DeviceService} from "../device.service";

enum State {
	ShowUpgrades,
	UpgradeSuccess,
	UpgradeFailure
}

@Component({
	selector: "activate-ccd-synchronize-terrain-step",
	template: `
		<modal (close)="onModalClosed()"
			   modalClasses="modal-wide"
			   [clickToClose]="false"
			   [escapeToClose]="false">
			<header class="modal_header text-center">
				<h3 *ngIf="currentState == State.ShowUpgrades">Upgrade Device Terrain Databases</h3>
				<h3 data-testid="upgradeStatus" *ngIf="currentState == State.UpgradeSuccess">Success</h3>
				<h3 data-testid="upgradeStatus" *ngIf="currentState == State.UpgradeFailure">Error</h3>
			</header>
			<div class="modal_body px-4 text-center modal_body-vertical-padding">
				<!-- Upgrade state -->
				<div *ngIf="currentState == State.ShowUpgrades">
					<div style="padding-bottom: 1em; padding-top: 1em;">
						<strong>All Device Terrain Databases must match.</strong>
					</div>
					<div>
						Installing <i>4.9</i> arc second (higher quality) terrain databases for all devices is recommended. Your aircraft is eligible for this free upgrade.
					</div>
					<div style="padding-top: 1em; padding-bottom: 3em;">
						The following Devices are eligible for a free upgrade:
						<div>
							<table class="deviceTable" data-testid="devicesToUpgrade">
								<thead>
								<th>Device Name</th>
								<th>Model</th>
								<th>System ID</th>
								</thead>
								<tbody>
								<tr *ngFor="let device of requiredUpgradeDevices">
									<td>{{device.name}}</td>
									<td>{{device.model.name}}</td>
									<td>{{device.serial}}</td>
								</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div *ngIf="upgradeInProgress">
						<fly-loading [message]="'Upgrading...'"></fly-loading>
					</div>
				</div>
				<!-- Upgrade success state -->
				<div *ngIf="currentState == State.UpgradeSuccess">
					<div style="padding-bottom: 1em; padding-top: 1em;">
						<strong>Terrain Databases were successfully upgraded.</strong>
					</div>
					<div style="padding-top: 1em; padding-bottom: 3em;">
						<div>
							<div>
								<table class="deviceTable" data-testid="successfulUpgrades">
									<thead>
									<th>Device Name</th>
									<th>Model</th>
									<th>System ID</th>
									<th>Upgrade Status</th>
									</thead>
									<tbody>
									<tr *ngFor="let device of requiredUpgradeDevices">
										<td>{{device.name}}</td>
										<td>{{device.model.name}}</td>
										<td>{{device.serial}}</td>
										<td style="color: #33cc33;">&#10004;</td>
									</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<!-- Upgrade failure state -->
				<div *ngIf="currentState == State.UpgradeFailure">
					<div style="padding-bottom: 1em; padding-top: 1em;">
						<strong>Error Upgrading Terrain Databases.</strong>
					</div>
					<div style="padding-top: 1em; padding-bottom: 3em;">
						The following Devices <i>failed</i> to upgrade to <i>4.9</i> arc second Terrain Databases:
					</div>
					<div>
						<table class="deviceTable" data-testid="failedUpgrades">
							<thead>
							<th>Device Name</th>
							<th>Model</th>
							<th>System ID</th>
							<th>Upgrade Status</th>
							</thead>
							<tbody>
							<tr *ngFor="let device of failedDevices">
								<td>{{device.name}}</td>
								<td>{{device.model.name}}</td>
								<td>{{device.serial}}</td>
								<td>&#10060;</td>
							</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<footer class="modal_footer">
				<div *ngIf="currentState == State.ShowUpgrades">
					<button (click)="onModalClosed()" class="button button-secondary py-1 px-5">CANCEL</button>
					<button class="button button-primary py-1 px-5 modal_buttons" data-testid="upgradeButton"
							(click)="upgrade()"
							[disabled]="upgradeInProgress">
						{{upgradeInProgress ? "UPGRADING..." : "UPGRADE "}}
					</button>
				</div>
				<div *ngIf="currentState == State.UpgradeSuccess">
					<button class="button button-primary py-1 px-5 modal_buttons"
							(click)="continuePressed()">
						Continue
					</button>
				</div>
				<div *ngIf="currentState == State.UpgradeFailure">
					<button class="button button-primary py-1 px-5 modal_buttons"
							(click)="onModalClosed()">
						Close
					</button>
				</div>
			</footer>
		</modal>
	`
})
export class SynchronizeTerrainModal {
	@Input() requiredUpgradeDevices : Set<Device>;
	@Input() isDealerCustomerManagementFlow: boolean;
	@Input() grantorCustomerGuid: string; // used when isDealerCustomerManagementFlow
	@Output() modalClosed = new EventEmitter();
	@Output() upgradeSuccess = new EventEmitter();

	readonly State = State;
	currentState = State.ShowUpgrades;
	failedDevices: Set<Device> = new Set<Device>();
	upgradeInProgress: boolean = false;

	constructor(private deviceService : DeviceService) {
	}

	ngOnInit() {
		this.requiredUpgradeDevices.forEach(device => {
			this.failedDevices.add(device);
		});
	}

	onModalClosed() {
		this.modalClosed.emit();
	}

	continuePressed() {
		this.upgradeSuccess.emit();
	}

	upgrade() {
		this.upgradeInProgress = true;
		let devicesLeftToUpgrade: number = this.requiredUpgradeDevices.size;

		this.requiredUpgradeDevices.forEach((device) => {
			this.deviceService.upgradeTerrainVersion(device.id, this.isDealerCustomerManagementFlow ? this.grantorCustomerGuid : null)
				.subscribe(
					() => {
						this.failedDevices.delete(device);
					},
					() => {}
				)
				.add(() => {
					devicesLeftToUpgrade--;
					if (devicesLeftToUpgrade == 0) {
						this.upgradeInProgress = false;

						// if there are any devices remaining in here then there was an error upgrading one of them
						if (this.failedDevices.size > 0) {
							this.currentState = State.UpgradeFailure;
						} else {
							this.currentState = State.UpgradeSuccess;
						}
					}
				});
		});
	}
}
